import { useRef, useState, useCallback, useEffect } from "react";
import { apiHttp } from "../../lib/appConfig";
import { useUser } from "../../features/User/userQueries";
import "./IFrameChat.css";

const CHAT_URL = process.env.REACT_APP_LIVE_CHAT_URL;

/**
 * Self-hosted live-chat widget. Mirrors the next platform's IFrameChat:
 * fetches a short-lived chat JWT from v1/auth/live-chat-token, opens the
 * chat app in an iframe with the token, toggled by a floating button.
 */
const IFrameChat = () => {
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUrl, setChatUrl] = useState<string | null>(null);
  const [iframeReady, setIframeReady] = useState(false);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const { data: user } = useUser();

  useEffect(() => {
    if (!user || !CHAT_URL) return;

    let cancelled = false;
    const fetchToken = async () => {
      try {
        const res = await apiHttp.get("v1/auth/live-chat-token");
        const token = res?.data?.data?.token;
        if (token && !cancelled) {
          const url = new URL(CHAT_URL);
          url.searchParams.set("chat_token", encodeURIComponent(token));
          setChatUrl(url.href);
        }
      } catch (err) {
        console.error("[LiveChat] Failed to get chat token:", err);
      }
    };

    fetchToken();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleIframeLoad = useCallback(() => setIframeReady(true), []);
  const toggleChat = useCallback(() => setChatOpen((prev) => !prev), []);

  if (!user || !chatUrl) return null;

  return (
    <>
      <div className={`live-chat-overlay${chatOpen ? " live-chat-overlay--open" : ""}`}>
        <div className="live-chat-container">
          <button className="live-chat-close" onClick={toggleChat} aria-label="Close chat">
            ✕
          </button>
          <iframe
            ref={iframeRef}
            src={chatUrl}
            className="live-chat-iframe"
            title="Live Chat"
            onLoad={handleIframeLoad}
          />
        </div>
      </div>

      {iframeReady && (
        <button
          className="live-chat-fab"
          onClick={toggleChat}
          aria-label={chatOpen ? "Close chat" : "Open chat"}
        >
          {chatOpen ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          )}
        </button>
      )}
    </>
  );
};

export default IFrameChat;
